import {Injectable, NgZone} from '@angular/core';
import {Action, State, StateContext, StateToken, Selector, Store} from '@ngxs/store';
import {catchError, tap} from 'rxjs/operators';
import {
  GetAllCountriesListAction,
  GetBankListAction, GetBusinessDocumentTypesListAction, GetCantidadHorasVtoOfertasAction, GetChequeOfertaStatusAction,
  GetDocumentationLinksAction, GetDocumentsTypeListAction, GetEstadoChequeListAction,
  GetInstructiveDocumentAction, GetInstructiveDocumentTypeAction,
  GetSocialMediaLinksAction,
  GetTipoChequeListAction, GetTransaccionTypeListAction
} from '../actions/masters.actions';
import {Bank} from '../models/bank.model';
import {Observable, of, throwError} from 'rxjs';
import {MastersApiService} from '../api/masters.api.service';
import {EstadoCheque, TipoCheque} from '../models/cheque.model';
import {InstructiveType} from '../models/instructive.model';
import {PhoneCountryCode} from '../models/phone-country-code.model';
import {TransactionType} from '../models/transaction-type.model';
import {BusinessDocumentTypeModel} from '../models/business-document-type.model';
import { SocialLinksMapped } from '../models/social-links.model';

export interface MastersStateModel {
  bankList: Bank[];
  tipoChequeList: TipoCheque[];
  instructiveTypeList: InstructiveType[];
  documentationLinks: any;
  linkTemp: string;
  estadoChequeList: EstadoCheque[];
  phoneCountryCodeList: PhoneCountryCode[];
  transactionTypeList: TransactionType[];
  businessDocumentTypeList: BusinessDocumentTypeModel[];
  documentsTypeList: any[];
  chequeOfertaStatus: any[];
  socialMediaLinks: SocialLinksMapped;
}

const initialState: MastersStateModel = {
  bankList: null,
  tipoChequeList: null,
  documentationLinks: null,
  linkTemp: null,
  instructiveTypeList: null,
  estadoChequeList: null,
  phoneCountryCodeList: null,
  transactionTypeList: null,
  businessDocumentTypeList: null,
  documentsTypeList: null,
  chequeOfertaStatus: null,
  socialMediaLinks: null
};

const MASTERS_STATE_TOKEN = new StateToken<MastersStateModel>('masters');

@State<MastersStateModel>({
  name: MASTERS_STATE_TOKEN,
  defaults: initialState
})
@Injectable()
export class MastersState {

  constructor(
    private store: Store,
    private mastersApiService: MastersApiService,
    private ngZone: NgZone
  ) {
  }

  @Action(GetBankListAction)
  handleGetBankListAction(ctx: StateContext<MastersStateModel>) {

    ctx.patchState({
      bankList: null
    });

    return this.mastersApiService.getBankList().pipe(
      tap(bankList => {
        const state = ctx.getState();

        ctx.patchState({
          bankList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );

  }

  @Action(GetTipoChequeListAction)
  handleGetTipoChequeListAction(ctx: StateContext<MastersStateModel>) {

    ctx.patchState({
      tipoChequeList: null
    });

    return this.mastersApiService.getTipoChequeList().pipe(
      tap(tipoChequeList => {
        const state = ctx.getState();

        ctx.patchState({
          tipoChequeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );

  }

  @Action(GetDocumentationLinksAction)
  handleGetAccountDocumentationLinksAction(ctx: StateContext<MastersStateModel>, action: GetDocumentationLinksAction) {

    let userType = action.payload.userType;

    return this.mastersApiService.getDocumentationLinks(userType).pipe(
      tap(linkResp => {
        const state = ctx.getState();
        ctx.patchState({
          documentationLinks: linkResp.link
        });
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetInstructiveDocumentAction)
  handleGetInstructiveDocumentAction(ctx: StateContext<MastersStateModel>, action: GetInstructiveDocumentAction) {
    let id = action.payload.id;
    return this.mastersApiService.getInstructiveById(id).pipe(
      tap(response => {
        ctx.patchState({
          linkTemp: response.linkTmp
        });
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetInstructiveDocumentTypeAction)
  handleGetInstructiveDocumentTypeAction(ctx: StateContext<MastersStateModel>, action: GetInstructiveDocumentTypeAction) {
    return this.mastersApiService.getInstructionTypeList().pipe(
      tap(response => {
        ctx.patchState({
          instructiveTypeList: response
        });
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetSocialMediaLinksAction)
  handleGetSocialMediaLinksAction(ctx: StateContext<MastersStateModel>, action: GetSocialMediaLinksAction) {
    return this.mastersApiService.getSocialMediaLinks().pipe(
      tap(response => {
        ctx.patchState({
          socialMediaLinks: response
        });
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetEstadoChequeListAction)
  handleGetEstadoChequeListAction(ctx: StateContext<MastersStateModel>) {

    ctx.patchState({
      estadoChequeList: null
    });

    return this.mastersApiService.getEstadoChequeList().pipe(
      tap(estadoChequeList => {
        const state = ctx.getState();

        ctx.patchState({
          estadoChequeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );

  }

  @Action(GetAllCountriesListAction)
  handleGetAllCountriesListAction(ctx: StateContext<MastersStateModel>) {
    ctx.patchState({
      phoneCountryCodeList: null
    });
    return this.mastersApiService.getAllCountriesList().pipe(
      tap(phoneCountryCodeList => {
        const state = ctx.getState();
        ctx.patchState({
          phoneCountryCodeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetTransaccionTypeListAction)
  handleGetTransaccionTypeListAction(ctx: StateContext<MastersStateModel>) {
    ctx.patchState({
      transactionTypeList: null
    });
    return this.mastersApiService.getTransactionsTypesList().pipe(
      tap(transactionTypeList => {
        const state = ctx.getState();
        ctx.patchState({
          transactionTypeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetBusinessDocumentTypesListAction)
  handleGetBusinessDocumentTypesListAction(ctx: StateContext<MastersStateModel>): Observable<GetBusinessDocumentTypesListAction> {
    ctx.patchState({
      businessDocumentTypeList: null
    });
    return this.mastersApiService.getBusinessDocumentTypes().pipe(
      tap(businessDocumentTypeList => {
        const state = ctx.getState();
        ctx.patchState({
          businessDocumentTypeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetDocumentsTypeListAction)
  handleGetDocumentsTypeListAction(ctx: StateContext<MastersStateModel>) {
    ctx.patchState({
      documentsTypeList: null
    });
    return this.mastersApiService.getDocumentsType().pipe(
      tap(documentsTypeList => {
        const state = ctx.getState();
        ctx.patchState({
          documentsTypeList
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  @Action(GetChequeOfertaStatusAction)
  handleGetChequeOfertaStatusAction(ctx: StateContext<MastersStateModel>) {
    ctx.patchState({
      chequeOfertaStatus: null
    });
    return this.mastersApiService.getChequeOfertaStatus().pipe(
      tap(chequeOfertaStatus => {
        const state = ctx.getState();
        ctx.patchState({
          chequeOfertaStatus
        });

      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }
  @Selector()
  static getBankList(state: MastersStateModel) {
    return state?.bankList;
  }

  @Selector()
  static getTipoChequeList(state: MastersStateModel) {
    return state?.tipoChequeList;
  }

  @Selector()
  static getEstadoChequeList(state: MastersStateModel) {
    return state?.estadoChequeList;
  }

  @Selector()
  static getDocumentationLinks(state: MastersStateModel) {
    return state?.documentationLinks;
  }

  @Selector()
  static getPhoneCountryCodeList(state: MastersStateModel) {
    return state?.phoneCountryCodeList;
  }

  @Selector()
  static getTransactionTypeList(state: MastersStateModel) {
    return state?.transactionTypeList;
  }

  @Selector()
  static getBusinessDocumentTypeList(state: MastersStateModel) {
    return state?.businessDocumentTypeList;
  }

  @Selector()
  static getDocumentsTypeList(state: MastersStateModel) {
    return state?.documentsTypeList;
  }

  @Selector()
  static getChequeOfertaStatusList(state: MastersStateModel) {
    return state?.chequeOfertaStatus;
  }
}
